import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Menu, Search, X } from 'lucide-react'
import { AppSidebar } from '@/components/app-sidebar'
import { BrandMark } from '@/components/brand-mark'
import { EnvironmentPill } from '@/components/environment-pill'
import { Button } from '@/components/ui/button'
import { SEARCH_SHORTCUT_ARIA } from '@/hooks/use-global-search-hotkey'
import { useGlobalSearchStore } from '@/stores/global-search-store'

/** O `lg` do Tailwind: daqui para cima a moldura volta a ter o trilho fixo. */
const COM_TRILHO = '(min-width: 64rem)'

/**
 * O CABEÇALHO DO CELULAR, e a gaveta que ele abre (#493).
 *
 * Abaixo de `lg` o trilho de 232px deixava ~150px de palco num celular de 390.
 * Aqui ele some, e o MESMO menu (`AppSidebar` em variante `drawer`) passa a
 * morar numa gaveta à esquerda, aberta pelo botão deste cabeçalho. A partir de
 * `lg` o cabeçalho não existe (`lg:hidden`) e a moldura é a de sempre.
 *
 * ────────────────────────────────────────────────────────────────────────
 * AS DECISÕES QUE NÃO SE VEEM NO ECRÃ
 * ────────────────────────────────────────────────────────────────────────
 *
 * 1. ⚠️ A GAVETA É PORTADA PARA O `<body>` (`DialogPrimitive.Portal`), NUNCA
 *    FICA DENTRO DO CABEÇALHO. O cabeçalho é `glass`, e `backdrop-filter` faz
 *    dele o containing block dos descendentes `fixed` E RECORTA-OS à sua
 *    border-box (medido no `ui/dialog.tsx`): uma gaveta montada aqui ficava
 *    presa aos 56px do cabeçalho.
 *
 * 2. É O DIÁLOGO DO RADIX, E NÃO O `ui/dialog.tsx` NATIVO. O nativo fixa
 *    `m-auto max-w-md` (centrado e estreito, o contrário de uma gaveta) e fecha
 *    de golpe, sem animação de saída. O Radix dá o resto do contrato de graça:
 *    foco preso, `Esc`, clique no véu, foco de volta ao botão, `aria-expanded`
 *    e `aria-controls` no gatilho, e espera pela animação de saída antes de
 *    desmontar. (O popover aninhado tem uma história — ver o 7.)
 *
 * 3. A GAVETA E A PALETA NUNCA COEXISTEM — e a ordem é: a gaveta SAI, depois a
 *    paleta ENTRA. Enquanto a gaveta está no DOM (aberta, ou nos 220 ms da
 *    animação de saída: o Radix só a desmonta no fim), o Radix põe
 *    `aria-hidden` no resto da página — onde vive a paleta, um `<dialog>` do
 *    `AppLayout`. Aberta nessa janela, a paleta ficava muda para o leitor de
 *    ecrã; e o `<dialog>` gravava como «elemento anterior» o «Buscar» da gaveta,
 *    que desmonta — ao fechar a paleta o foco caía no `<body>`. (O
 *    `pointer-events: none` no `<body>` só dura enquanto a gaveta está ABERTA,
 *    desde o `react-dialog` 1.1.23 — #502; antes durava também a saída.)
 *    Por isso um pedido de busca com a gaveta montada (o «Buscar» de lá, ou o
 *    `⌘K`) é ADIADO: a gaveta fecha, o foco volta ao «Abrir menu», e só quando
 *    ela já saiu do DOM (`onCloseAutoFocus`) a paleta abre. Sem gaveta, a busca
 *    abre na hora, como sempre.
 *    Quem decide se a busca espera é a gaveta EXISTIR no DOM (o `ref` do
 *    conteúdo), e não ela ter sido pedida: um pedido de abrir desfeito no mesmo
 *    lote (uma navegação, a passagem para `lg`) nunca monta a gaveta nem chama o
 *    `onCloseAutoFocus`, e uma marca armada no pedido ficava presa — a busca
 *    morria, adiada à espera de um fecho que não vinha.
 *    Dois casos que ficam assim, de propósito: reabrir a gaveta durante a saída
 *    DESCARTA a busca adiada (o pedido valia para aquele fecho); e um segundo
 *    `⌘K` durante a saída não a desfaz (não há paleta aberta para fechar — o
 *    pedido continua de pé).
 *
 * 4. FECHA A CADA NAVEGAÇÃO, e quem o diz é a CHAVE da localização, não o
 *    `pathname`: tocar no item da página onde já se está é uma navegação (o
 *    React Router substitui a entrada), e o caminho não muda.
 *
 * 5. FECHA AO PASSAR PARA `lg` (rodar um tablet). Aí o cabeçalho some, e uma
 *    gaveta modal aberta ficava por cima do trilho que voltou, sem o botão a que
 *    o foco regressa. Esconder a gaveta com `lg:hidden` seria pior: um modal
 *    invisível a prender o foco e os cliques da página inteira.
 *
 * 6. UMA SUPERFÍCIE SÓ. A gaveta é chrome FLUTUANTE (paira sobre o palco, com
 *    véu), logo é `glass-float` — numa camada IRMÃ do conteúdo, o padrão do
 *    `ui/dialog.tsx`: nenhum descendente fica dentro do `backdrop-filter`. O
 *    `<nav>` lá dentro larga o vidro dele. O cabeçalho é chrome FIXO, logo
 *    `glass`, como o trilho que substitui.
 *
 * 7. ⚠️ O POPOVER DO CARTÃO DO USUÁRIO É PORTADO PARA DENTRO DA GAVETA (o
 *    `portalContainer` do `AppSidebar`), e não para o `<body>`. Nasceu assim
 *    porque o `@radix-ui/react-dialog` e o `@radix-ui/react-popover` resolviam
 *    versões DIFERENTES do `react-focus-scope` e do `react-dismissable-layer`, e
 *    a armadilha de foco da gaveta não via o popover: fora dela, ele abria e
 *    fechava logo a seguir (e herdava o `pointer-events: none` do resto da
 *    página). Dentro, é conteúdo da gaveta. O nó é IRMÃO da camada de vidro,
 *    nunca descendente dela — a R4 do `vidro-do-chrome.test.ts`.
 *    O #502 alinhou as versões: um popover portado para o `<body>` já funciona
 *    dentro de um diálogo do Radix, e com o popover aberto um `Esc` fecha só o
 *    popover. O portal para dentro fica: é o desenho que o #493 verificou em
 *    produção.
 */
export function CabecalhoDoCelular() {
  const [aberto, setAberto] = useState(false)
  // (7) O nó DENTRO da gaveta para onde o popover do cartão do usuário é portado.
  const [superficie, setSuperficie] = useState<HTMLDivElement | null>(null)
  // (3) O nó da gaveta: não-nulo enquanto ela está no DOM — aberta OU a sair (o
  // `Presence` do Radix só a desmonta no fim da animação de saída).
  const conteudo = useRef<HTMLDivElement>(null)
  // (3) Um pedido de busca que chegou com a gaveta no DOM, à espera de ela sair.
  const buscaPendente = useRef(false)
  const { key } = useLocation()
  // O MESMO interruptor que o `⌘K` e o «Buscar» do trilho mexem.
  const abrirBusca = useGlobalSearchStore((s) => s.open)

  const mudarAberto = (valor: boolean) => {
    // Um pedido pendente valia para o fecho que o criou, não para o seguinte.
    if (valor) buscaPendente.current = false
    setAberto(valor)
  }

  // (4) Cada navegação fecha a gaveta.
  useEffect(() => {
    setAberto(false)
  }, [key])

  // (3) A moldura a desmontar com uma busca adiada (sair da sessão, por
  // exemplo): o Radix ainda chama o `onCloseAutoFocus` num `setTimeout`, e sem
  // isto abria a paleta numa loja de módulo — que saltava na próxima moldura.
  useEffect(
    () => () => {
      buscaPendente.current = false
    },
    [],
  )

  // (3) Pedir a busca com a gaveta montada ADIA-A: a gaveta fecha primeiro, e a
  // paleta abre no `onCloseAutoFocus`, abaixo. As subscrições do zustand correm
  // DENTRO do `set`, antes de qualquer render — desfazer aqui o `isOpen` faz
  // que a paleta nunca chegue a abrir por cima da gaveta.
  useEffect(
    () =>
      useGlobalSearchStore.subscribe((s, antes) => {
        if (!s.isOpen || antes.isOpen || conteudo.current === null) return
        buscaPendente.current = true
        useGlobalSearchStore.setState({ isOpen: false })
        setAberto(false)
      }),
    [],
  )

  // (5) Passar para `lg` fecha a gaveta. O jsdom não tem `matchMedia`.
  useEffect(() => {
    const consulta = window.matchMedia?.(COM_TRILHO)
    if (!consulta) return
    const aoMudar = (e: MediaQueryListEvent) => {
      if (e.matches) setAberto(false)
    }
    // O Safari < 14 só tem o par antigo (`addListener`, hoje obsoleto): lá, o
    // `addEventListener` lançava no mount e derrubava a moldura inteira.
    if (typeof consulta.addEventListener === 'function') {
      consulta.addEventListener('change', aoMudar)
      return () => consulta.removeEventListener('change', aoMudar)
    }
    consulta.addListener(aoMudar)
    return () => consulta.removeListener(aoMudar)
  }, [])

  return (
    <DialogPrimitive.Root open={aberto} onOpenChange={mudarAberto}>
      {/* `border-x-0 border-t-0 border-b`: o `glass` declara `border` nos quatro
          lados, e encostado ao topo e às margens do ecrã só o de BAIXO separa
          alguma coisa (o mesmo acerto do cabeçalho de coluna do Kanban). A cor
          é a do fio do trilho, que este cabeçalho substitui. */}
      <header className="glass border-hair flex h-14 items-center gap-1 border-x-0 border-b border-t-0 px-2 lg:hidden">
        {/* `size-11`: 44px de alvo de toque (o `size="icon"` tem 36), e o ícone
            fica do tamanho de sempre. O mesmo nos outros dois botões. */}
        <DialogPrimitive.Trigger asChild>
          <Button variant="ghost" size="icon" aria-label="Abrir menu" className="size-11">
            <Menu aria-hidden="true" />
          </Button>
        </DialogPrimitive.Trigger>

        <div className="flex min-w-0 items-center gap-2.5 px-1">
          <BrandMark />
          <span
            className="text-ink font-semibold"
            style={{ fontSize: 15, letterSpacing: '-0.018em' }}
          >
            Caliber
          </span>
          <EnvironmentPill />
        </div>

        {/* A busca em forma de ÍCONE, e não a cápsula do trilho: a 390px a
            cápsula comia a marca. Abre um diálogo, não navega — daí o
            `aria-haspopup`; o atalho continua a ser anunciado a quem tem
            teclado (um tablet abaixo de `lg`). */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Buscar"
          aria-haspopup="dialog"
          aria-keyshortcuts={SEARCH_SHORTCUT_ARIA}
          onClick={abrirBusca}
          className="ml-auto size-11"
        >
          <Search aria-hidden="true" />
        </Button>
      </header>

      <DialogPrimitive.Portal>
        {/* O véu do `ui/dialog.tsx`: 22% de tinta e 14px de desfoque. Aqui é um
            elemento de verdade (o Radix não usa `::backdrop`), e não leva vidro
            nenhum — por isso o `backdrop-blur` não colide com nada. */}
        <DialogPrimitive.Overlay className="menu-veu fixed inset-0 z-50 bg-black/[0.22] backdrop-blur-[14px]" />
        <DialogPrimitive.Content
          ref={conteudo}
          // Sem descrição: o título «Menu» e a navegação dizem tudo. Escrito por
          // extenso porque o Radix avisava da falta; desde o `react-dialog`
          // 1.1.23 (#502) o aviso já não existe, e o atributo só aparece quando
          // há `Description` — isto fica como declaração de intenção.
          aria-describedby={undefined}
          onCloseAutoFocus={() => {
            // O Radix chama isto já com a gaveta FORA do DOM: sem `aria-hidden`
            // no resto da página nem `pointer-events: none` no `<body>`.
            if (!buscaPendente.current) return
            buscaPendente.current = false
            // (3) A busca adiada. ⚠️ Sem `preventDefault`: logo a seguir a este
            // handler o Radix devolve o foco ao «Abrir menu», e a paleta só abre
            // no render seguinte — por isso é o botão que o `<dialog>` grava como
            // «elemento anterior», e fechar a paleta volta a ele, não ao `<body>`.
            abrirBusca()
          }}
          // `h-dvh`, e não `h-screen` nem `inset-y-0`: no celular o `100vh` é a
          // janela GRANDE (sem a barra do browser), e o cartão do usuário, no
          // fundo da gaveta, ficava por baixo dela — o mesmo que o
          // `portal-layout.tsx` já documenta.
          className="menu-gaveta fixed left-0 top-0 z-50 h-dvh w-[min(280px,85vw)] rounded-r-xl outline-none"
        >
          {/* A camada de vidro, IRMÃ do conteúdo (ver o `ui/dialog.tsx`). */}
          <div aria-hidden="true" className="glass-float absolute inset-0 z-0 rounded-[inherit]" />
          <div ref={setSuperficie} className="relative z-10 h-full">
            <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
            {/* Primeiro no DOM, e por isso é ele que recebe o foco ao abrir. À
                vista porque no celular não há `Esc`. Alinhado ao centro da marca
                do trilho (20px de topo + 15 de meia marca − 22 de meio botão). */}
            <DialogPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Fechar menu"
                className="absolute right-2 top-[13px] size-11"
              >
                <X aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
            <AppSidebar variant="drawer" portalContainer={superficie} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
